import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import { matchesSearch } from '../../utils/text';

const formatFee = (fee) => (fee != null ? `${Number(fee).toLocaleString('tr-TR')} ₺` : 'tanımsız');

export default function RoomList() {
  const { currentUser } = useAuth();
  const { data, fetchData, refreshData, loading, incrementWrites } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const canWrite = currentUser?.permissions?.write;
  const canDelete = currentUser?.permissions?.delete;

  useEffect(() => {
    fetchData('roomTypes');
  }, [fetchData]);

  const rooms = Array.isArray(data.roomTypes) ? data.roomTypes : [];

  const handleDelete = async (id) => {
    const room = rooms.find(r => r.id === id);
    if (!window.confirm(`"${room?.name || id}" oda tipini silmek istediğinize emin misiniz?\n\nBu odanın Fiyat Takvimi'ndeki TÜM fiyatları da geri dönüşsüz silinir. Bu odayı içeren eski teklifler görüntülenmeye devam eder, ancak düzenlenirken yeni bir oda seçilmesi gerekir.`)) return;
    try {
      // Yazma istekleri token ister; eskiden gönderilmediği için silme sessizce başarısız oluyordu
      const headers = jsonAuthHeaders(currentUser?.token);
      const res = await fetch(`${API_URL}/roomTypes/${id}`, { method: 'DELETE', headers });
      await readJsonOrThrow(res, 'Oda silinemedi.');
      incrementWrites(2); // Silme + Log
      await fetch(`${API_URL}/activityLogs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'Oda Silindi',
          description: `"${room?.name || id}" odası silindi.`,
          performedBy: { uid: currentUser.uid, email: currentUser.email }
        })
      });
      refreshData('roomTypes');
    } catch (error) {
      console.error("Silme hatası:", error);
      alert("Oda silinemedi: " + error.message);
    }
  };

  // Türkçe İ/ı ve büyük/küçük harf farkı önemsiz (toLowerCase "İ"yi bozuyordu)
  const filteredRooms = rooms.filter(room => matchesSearch([room.name], searchTerm));

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  if (loading.roomTypes && !data.roomTypes) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Oda Yönetimi</h1>
        {canWrite && (
        <Link
          to="/rooms/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Yeni Oda Ekle
        </Link>
        )}
      </div>

      {/* Arama Alanı */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Oda adı ile ara..."
            className="w-full pl-10 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Mobil Görünüm (Kartlar) */}
      <div className="md:hidden space-y-4">
        {filteredRooms.length === 0 ? (
            <div className="text-center p-4 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow">
                {rooms.length === 0 ? "Henüz oda tipi eklenmemiş." : "Aranan kriterlere uygun oda bulunamadı."}
            </div>
        ) : (
            filteredRooms.map((room) => (
                <div key={room.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                    <div 
                        className="p-4 flex justify-between items-center cursor-pointer"
                        onClick={() => toggleRow(room.id)}
                    >
                        <div>
                            <h3 className="font-bold text-gray-800 dark:text-white">{room.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{room.adultCapacity} Yetişkin / {room.childCapacity} Çocuk · 7-12 yaş: {formatFee(room.childFee)}</p>
                        </div>
                        {expandedRow === room.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                    </div>
                    
                    {expandedRow === room.id && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <div className="flex gap-2 mt-3 pt-3">
                                {canWrite && (
                                <Link to={`/rooms/edit/${room.id}`} className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-1.5 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 w-full justify-center">
                                    <Pencil size={16} /> Düzenle
                                </Link>
                                )}
                                {canDelete && (
                                <button onClick={() => handleDelete(room.id)} className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-1.5 rounded text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 w-full justify-center">
                                    <Trash2 size={16} /> Sil
                                </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ))
        )}
      </div>

      {/* Masaüstü Görünüm (Tablo) */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Oda Adı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Kapasite (Yetişkin/Çocuk)</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredRooms.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  {rooms.length === 0 ? "Henüz oda tipi eklenmemiş." : "Aranan kriterlere uygun oda bulunamadı."}
                </td>
              </tr>
            ) : (
              filteredRooms.map((room) => (
                <tr key={room.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-sm text-gray-900 dark:text-white">{room.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {room.adultCapacity} Yetişkin / {room.childCapacity} Çocuk
                    <span className="block text-xs text-gray-400">Çocuk ücreti (7-12 yaş): {formatFee(room.childFee)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canWrite && (
                    <Link to={`/rooms/edit/${room.id}`} className="text-blue-600 hover:text-blue-900 mr-4 inline-block">
                      <Pencil size={18} />
                    </Link>
                    )}
                    {canDelete && (
                    <button onClick={() => handleDelete(room.id)} className="text-red-600 hover:text-red-900 inline-block">
                      <Trash2 size={18} />
                    </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}